/**
 * Predictive Search Component
 *
 * Implements Shopify Predictive Search API following best practices from Dawn theme.
 * Simplified version without custom framework dependencies.
 *
 * API: /search/suggest.json
 * Docs: https://shopify.dev/docs/api/ajax/reference/predictive-search
 */

class PredictiveSearch {
    constructor() {
        this.searchInput = document.getElementById('searchInput');
        this.searchResults = document.getElementById('searchResults');
        this.searchOverlay = document.getElementById('searchOverlay');

        if (!this.searchInput || !this.searchResults) {
            console.warn('Predictive search elements not found');
            return;
        }

        this.abortController = null;
        this.searchTimeout = null;
        this.debounceDelay = 300; // ms

        this.init();
    }

    init() {
        // Render empty state on page load
        this.renderEmptyState();

        // Search input event
        this.searchInput.addEventListener('input', (e) => this.handleInput(e));

        // Clear search when modal closes
        this.setupModalResetListeners();
    }

    setupModalResetListeners() {
        // Listen for close button click
        const closeButton = document.getElementById('closeSearch');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                this.resetSearch();
            });
        }

        // Listen for overlay click (clicking outside modal)
        if (this.searchOverlay) {
            this.searchOverlay.addEventListener('click', (e) => {
                if (e.target === this.searchOverlay) {
                    this.resetSearch();
                }
            });
        }

        // Listen for escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.searchOverlay && !this.searchOverlay.classList.contains('hidden')) {
                this.resetSearch();
            }
        });
    }

    handleInput(event) {
        // Cancel previous search timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        const query = this.searchInput.value.trim();

        // If empty, show empty state
        if (!query) {
            this.renderEmptyState();
            return;
        }

        // Show loading state immediately
        this.renderLoadingState();

        // Debounce search
        this.searchTimeout = setTimeout(() => {
            this.performSearch(query);
        }, this.debounceDelay);
    }

    async performSearch(query) {
        // Cancel previous fetch if still running
        if (this.abortController) {
            this.abortController.abort();
        }

        // Create new abort controller for this request
        this.abortController = new AbortController();

        try {
            const url = this.buildSearchURL(query);

            const response = await fetch(url, {
                signal: this.abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.renderResults(data, query);

        } catch (error) {
            // Don't show error if request was aborted
            if (error.name === 'AbortError') {
                return;
            }

            console.error('Search error:', error);
            this.renderErrorState(error.message);
        }
    }

    buildSearchURL(query) {
        const url = new URL('/search/suggest.json', window.location.origin);

        // Shopify Predictive Search API parameters
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '4'); // 4 products per resource type
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        return url.toString();
    }

    renderResults(data, query) {
        const { resources } = data;

        if (!resources || !resources.results) {
            this.renderNoResults(query);
            return;
        }

        const products = resources.results.products || [];
        const collections = resources.results.collections || [];
        const pages = resources.results.pages || [];
        const articles = resources.results.articles || [];

        // If no results at all
        if (products.length === 0 && collections.length === 0 && pages.length === 0 && articles.length === 0) {
            this.renderNoResults(query);
            return;
        }

        // Check if we have any suggestions
        const hasSuggestions = collections.length > 0 || pages.length > 0 || articles.length > 0;

        // Render two-column layout (Products on left, Suggestions on right)
        this.searchResults.innerHTML = `
            <div class='predictive-search-results'>
                <div class='grid grid-cols-1 ${hasSuggestions ? 'lg:grid-cols-2' : ''} gap-6 lg:gap-8 mb-6'>
                    <!-- Left Column: Products -->
                    <div class='products-column ${hasSuggestions ? '' : 'col-span-full'}'>
                        <h3 class='text-sm font-axiom-demi-bold text-gray-500 uppercase tracking-wide mb-4'>
                            Products
                        </h3>
                        ${this.renderProducts(products)}
                    </div>

                    <!-- Right Column: Suggestions (only if not empty) -->
                    ${hasSuggestions ? `
                    <div class='suggestions-column'>
                        <h3 class='text-sm font-axiom-demi-bold text-gray-500 uppercase tracking-wide mb-4'>
                            Suggestions
                        </h3>
                        <div class='space-y-4'>
                            ${this.renderCollections(collections)}
                            ${this.renderPages(pages)}
                            ${this.renderArticles(articles)}
                        </div>
                    </div>
                    ` : ''}
                </div>

                <!-- View All Results Link -->
                <div class='border-t border-gray-200 pt-4'>
                    <a
                        href='/search?q=${encodeURIComponent(query)}'
                        class='flex items-center justify-center gap-2 w-full py-3 px-4 text-sm font-axiom-medium text-axiom-blue-dark hover:text-axiom-red-med transition-colors duration-200 group'
                    >
                        <span>Search for <span class='font-axiom-demi-bold'>"${this.escapeHTML(query)}"</span></span>
                        <svg
                            class='w-4 h-4 group-hover:translate-x-1 transition-transform duration-200'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                stroke-linecap='round'
                                stroke-linejoin='round'
                                stroke-width='2'
                                d='M9 5l7 7-7 7'
                            />
                        </svg>
                    </a>
                </div>
            </div>
        `;
    }

    renderCollections(collections) {
        if (!collections || collections.length === 0) return '';

        return `
            <div>
                <h4 class='text-xs font-axiom-medium text-gray-400 uppercase tracking-wide mb-2'>
                    Collections
                </h4>
                <ul class='space-y-1'>
                    ${collections.map(collection => `
                        <li>
                            <a
                                href='${collection.url}'
                                class='flex items-center gap-3 px-3 py-2 rounded-md text-sm text-axiom-blue-dark hover:bg-gray-50 hover:text-axiom-red-med transition-colors duration-200 group'
                            >
                                <svg
                                    class='w-4 h-4 text-gray-400 group-hover:text-axiom-red-med flex-shrink-0'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        stroke-width='2'
                                        d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
                                    />
                                </svg>
                                <span>${this.escapeHTML(collection.title)}</span>
                            </a>
                        </li>
                    `).join('')}
                </ul>
            </div>
        `;
    }

    renderPages(pages) {
        if (!pages || pages.length === 0) return '';

        return `
            <div>
                <h4 class='text-xs font-axiom-medium text-gray-400 uppercase tracking-wide mb-2'>
                    Pages
                </h4>
                <ul class='space-y-1'>
                    ${pages.map(page => `
                        <li>
                            <a
                                href='${page.url}'
                                class='flex items-center gap-3 px-3 py-2 rounded-md text-sm text-axiom-blue-dark hover:bg-gray-50 hover:text-axiom-red-med transition-colors duration-200 group'
                            >
                                <svg
                                    class='w-4 h-4 text-gray-400 group-hover:text-axiom-red-med flex-shrink-0'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        stroke-width='2'
                                        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                    />
                                </svg>
                                <span>${this.escapeHTML(page.title)}</span>
                            </a>
                        </li>
                    `).join('')}
                </ul>
            </div>
        `;
    }

    renderArticles(articles) {
        if (!articles || articles.length === 0) return '';

        return `
            <div>
                <h4 class='text-xs font-axiom-medium text-gray-400 uppercase tracking-wide mb-2'>
                    Articles
                </h4>
                <ul class='space-y-1'>
                    ${articles.map(article => `
                        <li>
                            <a
                                href='${article.url}'
                                class='flex items-center gap-3 px-3 py-2 rounded-md text-sm text-axiom-blue-dark hover:bg-gray-50 hover:text-axiom-red-med transition-colors duration-200 group'
                            >
                                <svg
                                    class='w-4 h-4 text-gray-400 group-hover:text-axiom-red-med flex-shrink-0'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        stroke-width='2'
                                        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                    />
                                </svg>
                                <span>${this.escapeHTML(article.title)}</span>
                            </a>
                        </li>
                    `).join('')}
                </ul>
            </div>
        `;
    }

    renderProducts(products) {
        if (!products || products.length === 0) {
            return `<p class='text-sm text-gray-500'>No products found</p>`;
        }

        return `
            <div class='grid grid-cols-1 gap-4'>
                ${products.map(product => `
                    <a
                        href='${product.url}'
                        class='flex gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group'
                    >
                        <!-- Product Image -->
                        <div class='flex-shrink-0 w-20 h-20 bg-gray-200 rounded-md overflow-hidden'>
                            ${product.image ? `
                                <img
                                    src='${product.image}'
                                    alt='${this.escapeHTML(product.title)}'
                                    class='w-full h-full object-cover'
                                    loading='lazy'
                                />
                            ` : `
                                <div class='w-full h-full flex items-center justify-center text-gray-400'>
                                    <svg class='w-8 h-8' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
                                    </svg>
                                </div>
                            `}
                        </div>

                        <!-- Product Info -->
                        <div class='flex-1 min-w-0'>
                            <h4 class='text-md font-axiom-medium mb-1'>
                                <span class='text-axiom-blue-dark group-hover:text-red-700 tracking-wide line-clamp-2 transition-colors duration-200'>
                                    ${this.escapeHTML(product.title)}
                                </span>
                            </h4>
                            ${product.body ? `
                                <p class='text-xs text-gray-500 mb-2 line-clamp-2'>
                                    ${this.stripHTML(product.body)}
                                </p>
                            ` : ''}
                            <div class='flex items-center gap-2 flex-wrap'>
                                <span class='text-gray-600 text-sm'>
                                    ${this.formatMoney(product.price)}
                                </span>
                                ${product.compare_at_price && product.compare_at_price > product.price ? `
                                    <span class='text-sm text-gray-500 line-through'>
                                        ${this.formatMoney(product.compare_at_price)}
                                    </span>
                                    <span class='text-xs font-axiom-demi-bold text-axiom-red-med'>
                                        Save ${Math.round((product.compare_at_price - product.price) / product.compare_at_price * 100)}%
                                    </span>
                                ` : ''}
                            </div>
                        </div>
                    </a>
                `).join('')}
            </div>
        `;
    }

    renderLoadingState() {
        const searchingText = this.searchResults.dataset.searching || 'Searching...';
        this.searchResults.innerHTML = `
            <div class='flex items-center justify-center py-12'>
                <div class='flex flex-col items-center gap-3'>
                    <svg class='animate-spin h-8 w-8 text-axiom-blue-dark' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                        <circle class='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' stroke-width='4'></circle>
                        <path class='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    <p class='text-sm text-gray-500'>${searchingText}</p>
                </div>
            </div>
        `;
    }

    renderEmptyState() {
        const emptyTitle = this.searchResults.dataset.emptyTitle || 'What are you looking for?';
        const emptyDescription = this.searchResults.dataset.emptyDescription || 'Start typing to search for bags, bike racks, and more...';
        const popularCategories = this.searchResults.dataset.popularCategories || 'Popular Categories';

        this.searchResults.innerHTML = `
            <div class='empty-state text-center py-12'>
                <div class='max-w-md mx-auto'>
                    <svg
                        class='w-20 h-20 mx-auto mb-6 text-gray-300'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                        <path
                            stroke-linecap='round'
                            stroke-linejoin='round'
                            stroke-width='1.5'
                            d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                        />
                    </svg>
                    <h3 class='text-lg font-axiom-demi-bold text-axiom-blue-dark mb-3'>
                        ${emptyTitle}
                    </h3>
                    <p class='text-sm text-gray-600 mb-6'>
                        ${emptyDescription}
                    </p>

                    <!-- Popular Categories -->
                    <div>
                        <h4 class='text-xs font-axiom-medium text-gray-400 uppercase tracking-wide mb-3'>
                            ${popularCategories}
                        </h4>
                        <div class='flex flex-wrap justify-center gap-2'>
                            ${this.getCategoryButtons()}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    getCategoryButtons() {
        // Get the count of collections from data attribute
        const collectionsCount = parseInt(this.searchResults.dataset.collectionsCount || '0');

        // If we have collections from the menu, use those
        if (collectionsCount > 0) {
            const buttons = [];
            for (let i = 1; i <= collectionsCount; i++) {
                const title = this.searchResults.dataset[`collection${i}Title`];
                const url = this.searchResults.dataset[`collection${i}Url`];

                if (title && url) {
                    buttons.push(`
                        <a
                            href='${url}'
                            class='inline-flex items-center px-4 py-2 rounded-full text-sm font-axiom-medium bg-gray-100 text-axiom-blue-dark hover:bg-axiom-red-med hover:text-white transition-colors duration-200'
                        >
                            ${title}
                        </a>
                    `);
                }
            }
            return buttons.join('');
        }

        // Fallback to hardcoded categories if no menu is configured
        const fallbackCategories = [
            { title: 'Panniers', url: '/collections/panniers' },
            { title: 'Handlebar Bags', url: '/collections/handlebar-bags' },
            { title: 'Trunk Bags', url: '/collections/trunk-bags' },
            { title: 'Frame Bags', url: '/collections/frame-bags' },
            { title: 'Accessories', url: '/collections/accessories' }
        ];

        return fallbackCategories.map(({ title, url }) => `
            <a
                href='${url}'
                class='inline-flex items-center px-4 py-2 rounded-full text-sm font-axiom-medium bg-gray-100 text-axiom-blue-dark hover:bg-axiom-red-med hover:text-white transition-colors duration-200'
            >
                ${title}
            </a>
        `).join('');
    }

    renderNoResults(query) {
        const noResultsTitle = this.searchResults.dataset.noResultsTitle || 'No results found';
        const noResultsMessage = this.searchResults.dataset.noResultsMessage || 'We couldn\'t find anything matching "{{ terms }}"';
        const noResultsSuggestion = this.searchResults.dataset.noResultsSuggestion || 'Try different keywords or browse our popular categories above';

        // Replace {{ terms }} placeholder with actual query
        const messageWithQuery = noResultsMessage.replace('{{ terms }}', `<span class='font-axiom-medium'>${this.escapeHTML(query)}</span>`);

        this.searchResults.innerHTML = `
            <div class='text-center py-12'>
                <svg
                    class='w-16 h-16 mx-auto mb-4 text-gray-300'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                    <path
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        stroke-width='1.5'
                        d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                    />
                </svg>
                <h3 class='text-lg font-axiom-demi-bold text-axiom-blue-dark mb-2'>
                    ${noResultsTitle}
                </h3>
                <p class='text-sm text-gray-600 mb-4'>
                    ${messageWithQuery}
                </p>
                <p class='text-xs text-gray-500'>
                    ${noResultsSuggestion}
                </p>
            </div>
        `;
    }

    renderErrorState(errorMessage) {
        this.searchResults.innerHTML = `
            <div class='text-center py-12'>
                <svg
                    class='w-16 h-16 mx-auto mb-4 text-red-300'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                    <path
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        stroke-width='2'
                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                </svg>
                <h3 class='text-lg font-axiom-demi-bold text-axiom-blue-dark mb-2'>
                    Something went wrong
                </h3>
                <p class='text-sm text-gray-600'>
                    Please try again later
                </p>
            </div>
        `;
    }

    resetSearch() {
        if (this.searchInput) {
            this.searchInput.value = '';
        }
        this.renderEmptyState();
    }

    // Utility functions
    escapeHTML(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    stripHTML(html) {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    }

    formatMoney(price) {
        // Price is already in decimal format from Predictive Search API (not cents)
        return `$${Number(price).toFixed(2)}`;
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    new PredictiveSearch();
});
